import { object, string, array } from 'yup';
import { BaseModel, CreateBaseModel } from './base.model';
import { JsonObject, UiOption } from '../helpers';
import * as yup from 'yup';
import { IdentProviderModel } from './ident-provider/ident-provider.model';
import { OrgMode, OrgModeOptions } from './org.model';

export type PartnerCertConfig = {
  useSurepathCA: boolean;
  customerPublicCA: string;
  lastUpdated: Date | null;
};

export type PartnerIdentityConfig = {
  gcipTenantId: string;
  providers: IdentProviderModel[]; // @todo add other provider types
};

export type PartnerBranding = {
  lightThemeLogo: string;
  portalBadge: {
    image: string;
    title: string;
  };
  portalWelcome: {
    title: string;
    message: string;
    learnMoreLink: string;
  };
  supportEmail: string;
  favicon: string;
  warnings: {
    publicServiceBlocked: string;
    publicServiceSensitiveData: string;
    privateServiceIntentBlocked: string;
  };
};

export type PartnerQuota = {
  maxTrialOrgs: number;
  maxPlatformOrgs: number;
};

// Root-granted capabilities. Only root users may change them; the API refuses anyone else.
export type PartnerOrgAction = 'create' | 'update' | 'delete';
export const PartnerOrgActions: PartnerOrgAction[] = ['create', 'update', 'delete'];
export const PartnerOrgActionLabels: Record<PartnerOrgAction, string> = {
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
};

// The form's shape: one boolean per action. The API stores an actions array, which the create model builds.
export type PartnerOrgModeSettings = {
  create: boolean;
  update: boolean;
  delete: boolean;
  seedData: boolean;
};

export type PartnerFeatures = {
  orgModes: Record<OrgMode, PartnerOrgModeSettings>;
};

type ApiOrgModeSettings = { actions?: PartnerOrgAction[]; seedData?: boolean };

const noActions: PartnerOrgModeSettings = { create: false, update: false, delete: false, seedData: false };

// A partner's users may do exactly what the block lists; no block means nothing, as the API treats it.
export const DefaultPartnerOrgModes: Record<OrgMode, PartnerOrgModeSettings> = {
  demo: noActions,
  platform: noActions,
  trial: noActions,
};

const orgModesFromApi = (
  orgModes: Partial<Record<OrgMode, ApiOrgModeSettings>> | undefined
): Record<OrgMode, PartnerOrgModeSettings> => {
  if (!orgModes) {
    return DefaultPartnerOrgModes;
  }
  const result = {} as Record<OrgMode, PartnerOrgModeSettings>;
  for (const { value } of OrgModeOptions) {
    const mode = value as OrgMode;
    const actions = orgModes[mode]?.actions || [];
    result[mode] = {
      create: actions.includes('create'),
      update: actions.includes('update'),
      delete: actions.includes('delete'),
      seedData: mode === 'demo' && orgModes[mode]?.seedData === true,
    };
  }
  return result;
};

const orgModesToApi = (orgModes: Record<OrgMode, PartnerOrgModeSettings>) => {
  const result: Partial<Record<OrgMode, ApiOrgModeSettings>> = {};
  for (const { value } of OrgModeOptions) {
    const mode = value as OrgMode;
    const settings = orgModes[mode];
    const actions = PartnerOrgActions.filter((action) => settings?.[action]);
    result[mode] = { actions, ...(mode === 'demo' && settings.seedData ? { seedData: true } : {}) };
  }
  return result;
};

export class PartnerModel extends BaseModel {
  name: string;
  domains: string[];
  certConfig: PartnerCertConfig;
  identityConfig: PartnerIdentityConfig;
  branding: PartnerBranding;
  quotas: PartnerQuota;
  features: PartnerFeatures;

  schema = object({
    name: string().required('Partner Name is required'),
    domains: array().of(yup.string()).min(1, 'Partners must have at least one domain'),
  });

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof PartnerModel, unknown>;

    this.name = (data.name as string) || '';
    this.domains = (data.domains as string[]) || [];
    this.certConfig = (data.certConfig as PartnerCertConfig) || {
      useSurepathCA: true,
      customerPublicCA: '',
      lastUpdated: null,
    };

    this.identityConfig = {
      gcipTenantId: '',
      providers: [],
    };

    if (data.identityConfig) {
      this.identityConfig = data.identityConfig as PartnerIdentityConfig;
      this.identityConfig.providers = this.identityConfig.providers.map(
        (data) => new IdentProviderModel(data as unknown as JsonObject)
      );
    }
    this.branding = (data.branding as PartnerBranding) || {
      lightThemeLogo: '',
      portalBadge: {
        image: '',
        title: '',
      },
      portalWelcome: {
        title: '',
        message: '',
        learnMoreLink: '',
      },
      supportEmail: '',
      favicon: '',
      warnings: {
        publicServiceBlocked: '',
        publicServiceSensitiveData: '',
        privateServiceIntentBlocked: '',
      },
    };

    this.quotas = (data.quotas as PartnerQuota) || {
      maxTrialOrgs: 100,
      maxPlatformOrgs: 100,
    };

    // Normalized so every mode has a settings object and the form controls have values.
    const features = (data.features as { orgModes?: Partial<Record<OrgMode, ApiOrgModeSettings>> }) || {};
    this.features = { orgModes: orgModesFromApi(features.orgModes) };
  }

  mayAct(mode: OrgMode, action: PartnerOrgAction): boolean {
    return this.features.orgModes[mode]?.[action] === true;
  }

  /** The modes on which the partner's users may take `action`, as select options. */
  modeOptionsFor(action: PartnerOrgAction): UiOption[] {
    return OrgModeOptions.filter(({ value }) => this.mayAct(value as OrgMode, action));
  }

  get hasOIDC(): boolean {
    return this.identityConfig.providers.some(({ type }) => type === 'OIDC');
  }

  get hasSAML(): boolean {
    return this.identityConfig.providers.some(({ type }) => type === 'SAML');
  }

  get domainUiOptions(): UiOption[] {
    return this.domains.map((domain) => ({
      label: `@${domain}`,
      value: domain,
    }));
  }
}

export class CreatePartnerModel extends CreateBaseModel {
  name: string;
  domains: string[];
  certConfig: PartnerCertConfig;
  branding: PartnerBranding;
  quotas: PartnerQuota;
  features?: { orgModes: Partial<Record<OrgMode, ApiOrgModeSettings>> };
  // creating identity config is not currently supported, and there is a unique key on tenant id, so leave this out for now

  constructor(partner: PartnerModel, isSuper = false) {
    super();

    this.name = partner.name;
    this.domains = partner.domains;
    this.certConfig = partner.certConfig;
    this.branding = partner.branding;
    this.quotas = partner.quotas;

    // Only root may set features, and the API refuses a partner user whose body changes them. A
    // partner user editing their own partner therefore sends none, so the stored block is untouched.
    if (isSuper) {
      this.features = { orgModes: orgModesToApi(partner.features.orgModes) };
    }
  }
}
